import get from "lodash.get";
import useApi from "./useApi";
import useEnv from "./useEnv";
import { useContext } from "react";
import { JobContext } from "../contexts/JobContext";

const useJobs = () => {
    const { setJob, job } = useContext(JobContext);
    const { getJson } = useApi();
    const { getEnv } = useEnv();

    /**
     * Fetches a job by its UUID and updates session storage
     * @param query
     */
    const fetchJobWithUuid = async (query) => {
        const _query = query ? `/${query}` : "";
        setJob(null);
        try {
            const res = await getJson(`/online/jobsearch/link${_query}`);
            setJob(res.job[0]);
            return res.job[0];
        } catch (error) {
            console.error("Error fetching job with UUID:", error);
            throw error;
        }
    };

    /**
     * Fetch the user's invoices/jobs with the given search params.
     * @param {Object} [params] - Search params, e.g. { include_paid, exclude_open, order, dir, search }
     * @returns {Promise<*[]>}
     */
    const fetchInvoices = async (params = {}) => {
        const query = new URLSearchParams(params).toString();
        try {
            const data = await getJson(`/online/govidsearch${query ? `?${query}` : ''}`);
            return data?.govidjob ?? [];
        } catch (error) {
            console.error("Error fetching invoices:", error);
            return [];
        }
    };

    /**
     * Fetch the user's open invoices/jobs (like the Dashboard invoices tab).
     * @returns {Promise<*[]>}
     */
    const fetchOpenInvoices = async () => {
        return fetchInvoices({ include_paid: 0, exclude_open: 0, order: 'paydate', dir: 'DESC' });
    };

    /**
     * Search the user's invoices/jobs by a search term (like the Dashboard search tab).
     * @param {string} searchTerm
     * @returns {Promise<*[]>}
     */
    const searchInvoices = async (searchTerm) => {
        return fetchInvoices({ include_paid: 1, exclude_open: 0, order: 'paydate', dir: 'DESC', search: searchTerm });
    };

    /**
     * Get a job's currency, or fall back to the environment default if the job has none set.
     * @param {object} job
     * @returns {string} currency
     */
    const getJobCurrency = (job) => {
        const currency = get(job, 'currency', false);
        return currency && currency !== '' ? currency : getEnv('DEFAULT_CURRENCY');
    };

    return {
        fetchJobWithUuid,
        fetchInvoices,
        fetchOpenInvoices,
        searchInvoices,
        getJobCurrency,
        job,
        setJob
    }
}

export default useJobs;
